import { useEffect, useId, useRef, useState } from 'react'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { AtSign, Check, Hash, Inbox, MessageSquare, Users, Volume2, X } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { useAppStore } from '@/store/app'
import { useGuildStore } from '@/store/guilds'
import { channelName, compareIds, useSocialStore } from '@/store/social'
import { inboxApi, type InboxEntry, type InboxMode } from '@/lib/inboxApi'
import { inboxEntryVisible, readThrough } from '@/lib/inbox'
import { describeError } from '@/lib/auth'
import { openChannelLink } from '@/lib/navigate'
import { cx } from '@/lib/cx'
import { ChannelType } from '@/types/api'
import { MessagePreview } from './MessagePreview'
import { MobileBack } from '../MobileBack'
import chat from '../ChatView.module.css'
import styles from './InboxPage.module.css'

export function InboxPage({ onClose, onNavigate }: { onClose?: () => void; onNavigate?: (() => void) | undefined } = {}) {
  const id = useId()
  const Root = onClose ? 'section' : 'main'
  const Heading = onClose ? 'h2' : 'h1'
  const me = useAuthStore((s) => s.user)
  const ready = useAppStore((s) => s.gateway === 'ready')
  const guilds = useGuildStore((s) => s.guilds)
  const channels = useGuildStore((s) => s.channels)
  const members = useGuildStore((s) => s.members)
  const settings = useGuildStore((s) => s.settings)
  const readStates = useSocialStore((s) => s.readStates)
  const privateChannels = useSocialStore((s) => s.channels)
  const relationships = useSocialStore((s) => s.relationships)
  const [mode, setMode] = useState<InboxMode>('unread')
  const [includeMuted, setIncludeMuted] = useState(false)
  const [unreadOnly, setUnreadOnly] = useState(true)
  const client = useQueryClient()
  const feed = useInfiniteQuery({
    queryKey: ['inbox', me?.id, mode, includeMuted, unreadOnly],
    queryFn: ({ pageParam }) => inboxApi.list({ mode, include_muted: includeMuted, unread_only: unreadOnly, before: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.has_more ? page.next_before ?? undefined : undefined,
    enabled: Boolean(me), staleTime: 10_000, refetchInterval: 30_000,
  })

  // События сообщений, ACK с другого устройства, изменения ролей и настроек обновляют ленту.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const refresh = () => {
      if (timer) return
      timer = setTimeout(() => { timer = undefined; void client.invalidateQueries({ queryKey: ['inbox', me?.id] }) }, 500)
    }
    const social = useSocialStore.subscribe((s, prev) => {
      if (s.readStates !== prev.readStates || s.channels !== prev.channels || s.messages !== prev.messages || s.relationships !== prev.relationships) refresh()
    })
    const guild = useGuildStore.subscribe((s, prev) => {
      if (s.guilds !== prev.guilds || s.channels !== prev.channels || s.members !== prev.members || s.settings !== prev.settings) refresh()
    })
    return () => { social(); guild(); clearTimeout(timer) }
  }, [client, me?.id])

  const seen = new Set<string>()
  const items = (feed.data?.pages.flatMap((p) => p.items) ?? []).map((entry) => ({ ...entry, messages: entry.messages.filter((m) => relationships[m.author.id]?.type !== 2) })).filter((entry) => {
    const key = mode === 'unread' ? entry.channel.id : entry.messages[0]?.id
    if (!key || seen.has(key)) return false
    seen.add(key)
    return me && inboxEntryVisible(entry, { userId: me.id, ready, mode, unreadOnly, includeMuted, guilds, channels, members, settings, privateChannels, readStates, relationships })
  })

  return (
    <Root className={cx(chat.chat, onClose && styles.panel)} aria-label="Входящие">
      <header className={chat.header}>
        {!onClose ? <MobileBack label="К списку бесед" /> : null}
        <Inbox size={24} className={chat.hash} />
        <Heading className={chat.title}>Входящие</Heading>
        {onClose ? <button type="button" className={styles.close} aria-label="Закрыть входящие" data-tooltip="Закрыть" onClick={onClose}><X size={20} /></button> : null}
      </header>
      <div className={styles.controls}>
        <div role="tablist" aria-label="Разделы входящих" className={styles.tabs} onKeyDown={(e) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return
          e.preventDefault()
          const next = e.key === 'Home' ? 'unread' : e.key === 'End' ? 'mentions' : mode === 'unread' ? 'mentions' : 'unread'
          setMode(next)
          e.currentTarget.querySelector<HTMLButtonElement>(`[data-mode="${next}"]`)?.focus()
        }}>
          <button type="button" role="tab" id={`${id}-unread-tab`} data-mode="unread" tabIndex={mode === 'unread' ? 0 : -1} aria-selected={mode === 'unread'} aria-controls={`${id}-feed`} className={cx(styles.tab, mode === 'unread' && styles.active)} onClick={() => setMode('unread')}>Непрочитанное</button>
          <button type="button" role="tab" id={`${id}-mentions-tab`} data-mode="mentions" tabIndex={mode === 'mentions' ? 0 : -1} aria-selected={mode === 'mentions'} aria-controls={`${id}-feed`} className={cx(styles.tab, mode === 'mentions' && styles.active)} onClick={() => setMode('mentions')}>Упоминания</button>
        </div>
        <div className={styles.filters}>
          <label><input type="checkbox" checked={includeMuted} onChange={(e) => setIncludeMuted(e.target.checked)} />Показывать заглушённые</label>
          {mode === 'mentions' ? <label><input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />Только непрочитанные</label> : null}
        </div>
      </div>
      <div id={`${id}-feed`} role="tabpanel" aria-labelledby={`${id}-${mode}-tab`} className={cx(styles.feed, 'scroller')}>
        <div className={styles.inner}>
          {feed.isPending ? <p className={styles.empty} role="status">Загружаем входящие…</p> : null}
          {feed.isError ? <div className={styles.empty}><p role="alert">{describeError(feed.error).message}</p><button type="button" className={styles.retry} onClick={() => void feed.refetch()}>Повторить</button></div> : null}
          {!feed.isPending && !feed.isError && !feed.hasNextPage && !items.length ? <div className={styles.empty}><Check size={36} aria-hidden="true" /><h2>{mode === 'unread' ? 'Всё прочитано' : 'Упоминаний нет'}</h2></div> : null}
          {items.map((entry) => <InboxCard key={mode === 'unread' ? entry.channel.id : entry.messages[0]!.id} entry={entry} mode={mode} onNavigate={onNavigate} read={readThrough(readStates[entry.channel.id], entry.last_message_id)} onRead={() => { void client.invalidateQueries({ queryKey: ['inbox', me?.id] }) }} />)}
          {feed.hasNextPage ? <button type="button" className={styles.more} disabled={feed.isFetching} onClick={() => void feed.fetchNextPage()}>{feed.isFetchingNextPage ? 'Загружаем…' : 'Показать ещё'}</button> : null}
        </div>
      </div>
    </Root>
  )
}

function InboxCard({ entry, mode, read, onRead, onNavigate }: { entry: InboxEntry; mode: InboxMode; read: boolean; onRead: () => void; onNavigate?: (() => void) | undefined }) {
  const [marking, setMarking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const pending = useRef(false)
  const ch = entry.channel
  const title = ch.guild_id ? ch.name ?? 'Канал' : channelName(ch)
  const Icon = ch.type === ChannelType.GuildVoice ? Volume2 : ch.type === ChannelType.PublicThread ? MessageSquare : ch.guild_id ? Hash : ch.type === ChannelType.GroupDM ? Users : AtSign
  const open = (messageId: string) => {
    onNavigate?.()
    void openChannelLink({ guildId: ch.guild_id ?? null, channelId: ch.id, messageId }).then((opened) => {
      if (opened) useAppStore.getState().showContent()
    })
  }
  const markRead = async () => {
    if (pending.current) return
    pending.current = true; setMarking(true); setError(null)
    try {
      const state = await inboxApi.markRead(ch.id, entry.last_message_id)
      useSocialStore.setState((s) => {
        const current = s.readStates[ch.id]
        // ACK уже мог прийти по шлюзу, а после него — новое сообщение. Ответ не затирает его счётчик.
        if (current?.last_message_id && state.last_message_id && compareIds(current.last_message_id, state.last_message_id) >= 0) return s
        return { readStates: { ...s.readStates, [ch.id]: state } }
      })
      onRead()
    } catch (err) { setError(describeError(err).message) }
    finally { pending.current = false; setMarking(false) }
  }
  return (
    <section className={styles.card} aria-label={`${entry.guild_name ? `${entry.guild_name} · ` : ''}${title}`}>
      <div className={styles.cardHeader}>
        <button type="button" className={styles.channel} onClick={() => open(entry.jump_message_id)}><Icon size={20} /><span><strong>{title}</strong><span className={styles.source}>{entry.guild_name || (ch.type === ChannelType.GroupDM ? 'Групповая беседа' : 'Личные сообщения')}</span></span></button>
        <button type="button" className={styles.mark} disabled={read || marking} aria-label={`Отметить ${title} прочитанным`} data-tooltip="Отметить канал прочитанным" onClick={() => void markRead()}><Check size={18} /><span>{marking ? 'Отмечаем…' : read ? 'Прочитано' : 'Отметить прочитанным'}</span></button>
      </div>
      {entry.messages.map((message) => <MessagePreview key={message.id} message={message} onOpen={() => open(message.id)} />)}
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      {mode === 'unread' ? <button type="button" className={styles.jump} onClick={() => open(entry.jump_message_id)}>К первому непрочитанному</button> : null}
    </section>
  )
}

